import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck } from "lucide-react";
import { CANDIDATE } from "@/data/content";
import { Marked, Asterisk, ArrowDraw, Star } from "@/components/campaign/HandDrawn";

const Donate = () => {
  return (
    <main data-testid="donate-page">
      <section className="section border-b border-ink/25">
        <div className="container-x grid gap-14 md:grid-cols-12">
          <div className="md:col-span-7">
            <p className="overline inline-flex items-center gap-2">
              <Asterisk className="h-4 w-4" /> Donate
            </p>
            <h1 className="mt-4 font-editorial font-semibold text-5xl md:text-7xl tracking-tight leading-[0.92] text-ink">
              <Marked>Chip in</Marked>. Power<br />
              the campaign.
            </h1>
            <p className="mt-10 text-lg leading-relaxed text-ink/80 max-w-xl">
              Every dollar funds doors knocked, postcards mailed, and a community-built campaign
              that doesn&apos;t take corporate or PAC money.{" "}
              <span className="italic font-semibold text-terracotta text-base">Secure processing</span>{" "}
              via ActBlue.
            </p>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-md" data-testid="donate-amounts">
              {[10, 25, 50, 100].map((amt) => (
                <a
                  key={amt}
                  href={CANDIDATE.actblueUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="border border-rule px-4 py-5 text-center text-ink font-display font-black text-2xl bg-white/5 hover:bg-terracotta hover:text-cream transition-colors duration-200 hover:shadow-[2px_2px_0_0_#3A72CC] hover:translate-x-[2px] hover:translate-y-[2px]"
                  data-testid={`donate-amount-${amt}`}
                >
                  ${amt}
                </a>
              ))}
            </div>

            <div className="mt-10 flex items-center gap-4 flex-wrap">
              <a
                href={CANDIDATE.actblueUrl}
                target="_blank"
                rel="noreferrer"
                className="btn-primary"
                data-testid="donate-actblue-btn"
              >
                Donate via ActBlue
                <ArrowUpRight size={18} />
              </a>
              <span className="italic font-semibold text-terracotta text-sm">Thank you</span>
              <ArrowDraw className="h-5 w-12" />
            </div>

            <p className="mt-6 text-sm text-ink/65 inline-flex items-center gap-2 font-medium">
              <ShieldCheck size={14} /> Secure processing via ActBlue. NH campaign-finance compliant.
            </p>
          </div>

          <aside className="md:col-span-5">
            <div className="bg-white/5 border border-rule p-7">
              <p className="overline inline-flex items-center gap-2">
                <Star className="h-4 w-4" /> Where your money goes
              </p>
              <ul className="mt-6 space-y-5 text-ink/85">
                <li className="flex justify-between border-b-2 border-ink/15 pb-3">
                  <span className="font-display font-black text-2xl">40%</span>
                  <span className="text-right">Field operations &amp; canvassing</span>
                </li>
                <li className="flex justify-between border-b-2 border-ink/15 pb-3">
                  <span className="font-display font-black text-2xl">25%</span>
                  <span className="text-right">Literature &amp; printed materials</span>
                </li>
                <li className="flex justify-between border-b-2 border-ink/15 pb-3">
                  <span className="font-display font-black text-2xl">20%</span>
                  <span className="text-right">Digital outreach &amp; events</span>
                </li>
                <li className="flex justify-between">
                  <span className="font-display font-black text-2xl">15%</span>
                  <span className="text-right">Compliance &amp; admin</span>
                </li>
              </ul>
            </div>
            <div className="mt-8 bg-parchment border border-rule p-7">
              <p className="overline">Other ways to give</p>
              <p className="mt-4 text-ink/80 leading-relaxed">
                Prefer a check? Make it payable to{" "}
                <span className="font-bold">Snipes4ThePeople</span> and mail to a P.O. Box we&apos;ll
                share via{" "}
                <Link to="/contact" className="text-terracotta font-bold underline decoration-2 underline-offset-4">
                  contact
                </Link>.
              </p>
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-ink text-cream section relative overflow-hidden">
        <div className="container-x text-center">
          <p className="overline !text-terracotta inline-flex items-center gap-2 justify-center">
            <Asterisk className="h-4 w-4" /> Not a donor?
          </p>
          <h2 className="mt-4 font-editorial font-semibold text-4xl md:text-6xl leading-[0.9]">
            Your <Marked color="#F58A3F">time</Marked> is worth<br />
            more than money.
          </h2>
          <p className="mt-8 text-cream/80 max-w-xl mx-auto text-lg">
            Two hours canvassing in your own neighborhood can move more votes than a TV ad.
          </p>
          <div className="mt-10">
            <Link to="/volunteer" className="btn-primary" data-testid="donate-volunteer-cta">
              Volunteer instead <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Donate;
